import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { colors, typography, radius, shadows } from './tokens';

// Shared component overrides that work in both modes (use theme callbacks where needed)
const baseThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: typography.fontFamily,
    h1: { fontSize: '2rem', fontWeight: typography.fontWeight.bold },
    h2: { fontSize: '1.5rem', fontWeight: typography.fontWeight.bold },
    h3: { fontSize: '1.25rem', fontWeight: typography.fontWeight.bold },
    h4: { fontSize: '1.125rem', fontWeight: typography.fontWeight.semibold },
    h5: { fontSize: '1rem', fontWeight: typography.fontWeight.semibold },
    h6: { fontSize: '0.875rem', fontWeight: typography.fontWeight.semibold },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
  },
  shape: { borderRadius: radius.sm },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeight.medium,
          borderRadius: radius.md,
        },
        containedPrimary: {
          '&:hover': { backgroundColor: colors.primary.hover },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    // Disable MUI's modal scroll-lock for transient popovers/menus. The lock
    // sets `overflow:hidden` on <body> and adds a compensating `padding-right`
    // for the scrollbar it expects to vanish — but the page already reserves
    // the scrollbar via `scrollbar-gutter: stable` (see index.css), so that
    // padding is spurious and shifts/narrows all page content when a Select
    // menu opens. Dialogs keep their lock (they want it).
    MuiPopover: {
      defaultProps: { disableScrollLock: true },
    },
    MuiMenu: {
      defaultProps: { disableScrollLock: true },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: radius.md },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: radius.lg },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm },
      },
    },
    // Drawer (sidebar) is always dark — mode-independent
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.sidebar.bg,
          color: colors.text.light,
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          margin: '1px 0',
          '&.Mui-selected': {
            backgroundColor: colors.primary.main,
            color: colors.sidebar.textActive,
            '&:hover': { backgroundColor: colors.primary.hover },
            '& .MuiListItemIcon-root': { color: colors.sidebar.textActive },
          },
          '&:hover': {
            backgroundColor: colors.sidebar.hoverBg,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: colors.sidebar.text,
          minWidth: 36,
        },
      },
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'light',
    primary: { main: colors.primary.main, light: colors.primary.light, dark: colors.primary.hover, contrastText: colors.primary.contrastText },
    secondary: { main: colors.secondary.main, light: colors.secondary.light, dark: colors.secondary.dark, contrastText: colors.secondary.contrastText },
    success: { main: colors.success.main, light: colors.success.light, dark: colors.success.dark, contrastText: '#fff' },
    error: { main: colors.error.main, light: colors.error.light, dark: colors.error.dark, contrastText: '#fff' },
    warning: { main: colors.warning.main, light: colors.warning.light, dark: colors.warning.dark, contrastText: '#161616' },
    info: { main: colors.info.main, light: colors.info.light, dark: colors.info.dark, contrastText: '#fff' },
    grey: colors.grey,
    background: { default: colors.background.page, paper: colors.background.paper },
    text: { primary: colors.text.primary, secondary: colors.text.secondary, disabled: colors.text.disabled },
    divider: colors.border.light,
  },
  components: {
    ...baseThemeOptions.components,
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: radius.lg, border: `1px solid ${colors.border.light}`, boxShadow: shadows.card },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: typography.fontWeight.semibold,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            color: colors.text.secondary,
            backgroundColor: colors.background.tableHead,
            borderColor: colors.border.light,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: colors.border.light, fontSize: typography.fontSize.base },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${colors.border.light}` },
      },
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...baseThemeOptions,
  palette: {
    mode: 'dark',
    primary: { main: colors.primary.main, light: colors.primary.light, dark: colors.primary.hover, contrastText: colors.primary.contrastText },
    secondary: { main: colors.secondary.main, light: colors.secondary.light, dark: colors.secondary.dark, contrastText: colors.secondary.contrastText },
    success: { main: colors.success.main, light: colors.success.light, dark: colors.success.dark, contrastText: '#fff' },
    error: { main: colors.error.main, light: colors.error.light, dark: colors.error.dark, contrastText: '#fff' },
    warning: { main: colors.warning.main, light: colors.warning.light, dark: colors.warning.dark, contrastText: '#161616' },
    info: { main: colors.info.main, light: colors.info.light, dark: colors.info.dark, contrastText: '#fff' },
    grey: colors.grey,
    background: { default: colors.background.darkPage, paper: colors.background.darkPaper },
    text: { primary: colors.text.darkPrimary, secondary: colors.text.darkSecondary, disabled: '#757575' },
    divider: colors.border.dark,
  },
  components: {
    ...baseThemeOptions.components,
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: radius.lg, border: `1px solid ${colors.border.dark}`, boxShadow: 'none' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: typography.fontWeight.semibold,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            color: colors.text.darkSecondary,
            backgroundColor: 'rgba(255,255,255,0.04)',
            borderColor: colors.border.dark,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: colors.border.dark, fontSize: typography.fontSize.base },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { borderTop: `1px solid ${colors.border.dark}` },
      },
    },
  },
});

export { colors, typography, radius, shadows } from './tokens';
